'use client'

import { useState, useEffect, Suspense } from 'react'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { createClient } from '@/lib/supabase/client'

function CastLoginInner() {
  const [subdomain, setSubdomain] = useState('')
  const [castId, setCastId] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const router = useRouter()
  const searchParams = useSearchParams()
  const supabase = createClient()

  // URLパラメータからサブドメインとキャストIDを自動入力
  useEffect(() => {
    const urlSubdomain = searchParams.get('subdomain')
    const urlCastId = searchParams.get('castId')

    if (urlSubdomain) {
      setSubdomain(urlSubdomain)
    }
    if (urlCastId) {
      setCastId(urlCastId)
    }
  }, [searchParams])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // 入力値の検証
    if (!subdomain.trim() || !castId.trim()) {
      setError('店舗サブドメインとキャストIDを入力してください')
      setLoading(false)
      return
    }

    try {
      // APIエンドポイントを使用してキャスト認証
      const response = await fetch('/api/cast-auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subdomain: subdomain.trim(),
          castId: castId.trim()
        })
      })

      const data = await response.json()

      if (!response.ok) {
        setError(data.error || '認証に失敗しました')
        return
      }

      if (!data.success) {
        setError('認証に失敗しました')
        return
      }

      // セッション情報をlocalStorageに保存（開発用）
      // 本番環境では、より安全な方法（JWT、セッションストレージ等）を使用
      localStorage.setItem('cast_session', JSON.stringify(data.session))

      // キャストダッシュボードにリダイレクト
      router.push(`/cast/${data.session.shop_id}/dashboard`)

    } catch (err) {
      console.error('Cast login error:', err)
      setError('ログインに失敗しました')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="h-12 w-12 rounded-lg bg-navy-600 flex items-center justify-center">
              <span className="text-white font-bold text-lg">C</span>
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            キャストログイン
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            店舗から発行されたキャストIDでログインしてください
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>キャストアカウントにログイン</CardTitle>
            <CardDescription>
              店舗サブドメインとキャストIDを入力してください
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="subdomain" className="block text-sm font-medium text-gray-700">
                  店舗サブドメイン <span className="text-red-500">*</span>
                </label>
                <Input
                  id="subdomain"
                  type="text"
                  value={subdomain}
                  onChange={(e) => setSubdomain(e.target.value)}
                  required
                  className="mt-1"
                  placeholder="your-shop"
                />
                <p className="mt-1 text-xs text-gray-500">
                  所属する店舗のサブドメインを入力してください
                </p>
              </div>

              <div>
                <label htmlFor="castId" className="block text-sm font-medium text-gray-700">
                  キャストID <span className="text-red-500">*</span>
                </label>
                <Input
                  id="castId"
                  type="text"
                  value={castId}
                  onChange={(e) => setCastId(e.target.value)}
                  required
                  className="mt-1"
                  placeholder="SH0001"
                />
                <p className="mt-1 text-xs text-gray-500">
                  店舗から発行されたキャストIDを入力してください（例：SH0001）
                </p>
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
              >
                {loading ? 'ログイン中...' : 'ログイン'}
              </Button>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">または</span>
                </div>
              </div>

              <div className="mt-6 text-center space-y-2">
                <Link
                  href="/login"
                  className="text-sm text-blue-600 hover:text-blue-500 font-medium"
                >
                  管理者・スタッフログイン
                </Link>
                <p className="text-xs text-gray-500">
                  キャストIDがわからない場合は店舗管理者にお問い合わせください
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default function CastLoginPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">読み込み中...</div>}>
      <CastLoginInner />
    </Suspense>
  )
}
